/**
 * Deterministic value noise and hashing.
 *
 * Everything the world contains is a pure function of its own coordinates and
 * the world seed. Nothing may depend on the camera, on time, or on the order in
 * which chunks happen to be visited. If you need randomness inside a chunk,
 * derive it from `chunkRng(seed, cx, cz, salt)` so the same chunk always
 * produces the same contents.
 */

/** 32-bit integer hash of three values. Stable across sessions and platforms. */
export function hash3(x: number, y: number, s: number): number {
  let n = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 1274126177);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return (n ^ (n >>> 16)) >>> 0;
}

/** Hash to the unit interval. */
export const hash01 = (x: number, y: number, s: number): number => hash3(x, y, s) / 4294967296;

/**
 * The name of a place, to a quarter of a metre — the naming corollary of the
 * law at the top of this file.
 *
 * A tree has no id because it does not need one: `world/chunk.ts` grows it
 * from position and seed, so two sessions, two machines and two centuries
 * agree about which tree this is as long as they agree about where it stood.
 * Quarter-metre resolution is far finer than the closest two plants ever get,
 * and coarse enough that float drift cannot rename one.
 *
 * It lives here rather than in `ui/attention.ts`, where it was first written,
 * because both the pointer (`ui`) and the store of things history has kept
 * (`world/things.ts`) have to agree about identity, and identity is a fact
 * about the world rather than about the interface. `ui/attention.ts`
 * re-exports it, so pointing at a thing and promoting it cannot drift apart.
 */
/**
 * A stable numeric seed from a string id — FNV-1a.
 *
 * Entities that carry their own ids (people, bands, structures) still need a
 * *number* to hash with, and parsing one back out of `band-3-h17` is the kind
 * of thing that breaks the day an id format changes. Lives here, next to the
 * determinism law, because both the simulation and the renderer need it and
 * the simulation may not reach into the renderer for anything.
 */
export function idSeed(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h | 0;
}

export const placeKey = (x: number, z: number): string =>
  `${Math.round(x * 4)}:${Math.round(z * 4)}`;

/** A small, fast, seedable generator. Same seed, same sequence, always. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The generator for one chunk's contents. Position in, identical results out. */
export const chunkRng = (seed: number, cx: number, cz: number, salt = 0): (() => number) =>
  mulberry32(hash3(cx, cz, seed + salt * 0x9e37));

const smooth = (t: number) => t * t * (3 - 2 * t);

export function valueNoise(x: number, y: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const u = smooth(x - xi), v = smooth(y - yi);
  const a = hash01(xi, yi, s), b = hash01(xi + 1, yi, s);
  const c = hash01(xi, yi + 1, s), d = hash01(xi + 1, yi + 1, s);
  const top = a + (b - a) * u;
  const bot = c + (d - c) * u;
  return top + (bot - top) * v;
}

export function fbm(x: number, y: number, octaves: number, s: number, lac = 2.02, gain = 0.5): number {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise(x * freq, y * freq, s + i * 17);
    norm += amp;
    amp *= gain;
    freq *= lac;
  }
  return sum / norm;
}

/** Ridged noise, for mountain chains rather than lumps. */
export function ridge(x: number, y: number, octaves: number, s: number): number {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(valueNoise(x * freq, y * freq, s + i * 31) * 2 - 1);
    sum += amp * n * n;
    norm += amp;
    amp *= 0.5;
    freq *= 2.03;
  }
  return sum / norm;
}

export const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
export const mixRgb = (
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
